/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   09/08/2022
 */

import React from "react";
import {
	Autocomplete,
	CircularProgress,
	FormControl,
	IconButton,
	Stack,
	TextField,
	Tooltip
} from "@mui/material";
import {useMutation, useQuery} from "urql";
import {
	slackUserConversationsQuery,
	SlackUserConversationsResponse
} from "../graphql/queries/slack";
import RepeatIcon from '@mui/icons-material/Repeat';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSlack} from "@fortawesome/free-brands-svg-icons";
import {SlackConversation} from "../interfaces/Global";
import {setSlackTokenMutation, SetSlackTokenResponse, SetSlackTokenVars} from "../graphql/mutations/slack";
import Const from "../const.json";
import {envType} from "../context/environment";
import {useDispatch, useSelector} from "react-redux";
import {State} from "../state/store";
import {add} from "../state/reducers/slack-user-token-scopes";
import {LoadingButton} from "@mui/lab";

export interface SlackChannelSelectorProps {
	channel?: SlackConversation
	error?: string
	onChange: (value: SlackConversation|null) => void
	redirectUri: string
	disabled?: boolean
}

const slackClientId = envType === "dev" ? Const.SLACK_APP_CLIENT_ID_DEV : Const.SLACK_APP_CLIENT_ID_PROD;
const slackScopes = ["channels:read", "channels:write", "groups:read", "groups:write"];
const slackAuthUrl = `https://slack.com/oauth/v2/authorize?user_scope=${slackScopes.join(",")}&client_id=${slackClientId}`;

export default function SlackUserChannelSelectorControl({ onChange, redirectUri, channel, error, disabled }: SlackChannelSelectorProps) {

	const dispatch = useDispatch();
	const slackTokenScopes = useSelector((s:State) => s.slackUserTokenScopes);
	const tokenSet = slackScopes.every(p => slackTokenScopes.includes(p));

	const [connectInProgress, setConnectInProgress] = React.useState(window.location.href.includes("?code="));

	const [userConversationsResult] = useQuery<SlackUserConversationsResponse>({
		query: slackUserConversationsQuery,
		pause: !tokenSet
	});

	const [setTokenResult, setToken] = useMutation<SetSlackTokenResponse, SetSlackTokenVars>(setSlackTokenMutation);

	React.useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search), code = urlParams.get("code");
		if (code) {
			setToken({ code, redirectUri }).then(response => {
				if (response.data?.setUserSlackToken.ok) {
					dispatch(add(slackScopes));
				}
			}).finally(() => {
				window.history.replaceState(null, "", window.location.pathname);
			});
		}
	}, [dispatch, redirectUri, setToken]);

	return (
		<Stack sx={{ mt: 1, ml: 4 }}>
			{ !tokenSet &&
				<Stack direction={"row"}>
					<LoadingButton
						variant={"outlined"}
						disabled={disabled}
						loading={connectInProgress}
						startIcon={<FontAwesomeIcon icon={faSlack} />}
						href={`${slackAuthUrl}&redirect_uri=${redirectUri}`}
						onClick={() => setConnectInProgress(true)}
					>
						Connect to Slack
					</LoadingButton>
				</Stack>}
			{ tokenSet &&
				<Stack>
					<FormControl variant={"standard"} error={!!error}>
						<Stack direction={"row"} justifyContent={"space-between"}>
							<Stack flex={1}>
								<Autocomplete
									defaultValue={channel}
									options={[
										...(userConversationsResult.data?.slackUserConversations.conversations || []),
										...(channel && !((userConversationsResult.data?.slackUserConversations.conversations || [])
											.some(i => i.id === channel.id)) ? [channel] : [])
									]}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									getOptionLabel={(option) => (option.isPrivate ? "🔒" : "") + option.name}
									onChange={(event, value) => {
										onChange(value);
									}}
									clearOnEscape
									filterSelectedOptions
									fullWidth
									disabled={disabled}
									loading={userConversationsResult.fetching || setTokenResult.fetching}
									renderInput={(params) => (
										<TextField
											{...params}
											variant="standard"
											label={"Slack Channel"}
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<React.Fragment>
														{userConversationsResult.fetching ? <CircularProgress color="inherit" size={20} /> : null}
														{params.InputProps.endAdornment}
													</React.Fragment>
												),
											}}
										/>
									)}
								/>
							</Stack>
							<Stack>
								<Tooltip title={"Reconnect to Slack"}>
									<IconButton
										disabled={disabled}
										href={`${slackAuthUrl}&redirect_uri=${redirectUri}`}
									>
										<RepeatIcon />
									</IconButton>
								</Tooltip>
							</Stack>
						</Stack>
					</FormControl>
				</Stack>}
		</Stack>
	);

}